import { logger } from "@fidget/common/utils";
import { config } from "@fidget/common/config/config";
import { IGuest } from "../models/base/Guest";
import { ProcessedField } from "./plugin";
import { PluginRegistry, PluginsConfiguration } from "./pluginRegistry";

interface DisplayableField {
  key: string;
  value: any;
  displayName?: string;
}

export class PluginService {
  private static instance: PluginService;
  private readonly registry: PluginRegistry;

  private constructor() {
    this.registry = new PluginRegistry();
  }

  static getInstance() {
    if (!PluginService.instance) {
      PluginService.instance = new PluginService();
    }
    return PluginService.instance;
  }

  hasActivePlugin(pluginId: string) {
    return this.registry.activePlugins.some((plugin) => plugin.id === pluginId);
  }

  connect(currentAgentId: string, orgId: string, currentAgentApiKey: string) {
    this.fetchPluginsConfiguration(orgId).then((plugins) =>
      this.registry.configure({ currentAgentId, orgId, currentAgentApiKey }, plugins)
    );
  }

  interpretUserData(user: IGuest): Array<DisplayableField> {
    return user.backOfficeData
      ? user.additionalData.map((field) => {
        const basicProcessedField: ProcessedField = { ...field }; // mobx's types.maybe says it's optional but it is string | undefined
        const processedField: ProcessedField = this.registry.activePlugins
          .filter((plugin) => !!plugin.onGuestField)
          .reduce((acc, plugin) => plugin.onGuestField!(acc, user), basicProcessedField);
        return {
          key: processedField.displayKey || processedField.key,
          value: processedField.displayValue || processedField.value,
          displayName: processedField.displayKey || processedField.displayName
        };
      })
      : [];
  }

  omniButtonClick(pluginId: string, guest: IGuest) {
    this.registry.activePlugins
      .filter((plugin) => plugin.id === pluginId && !!plugin.omniButtonClick)
      .forEach((plugin) => {
        plugin.omniButtonClick!(guest);
      });
  }

  private fetchPluginsConfiguration(orgId: string): Promise<PluginsConfiguration> {
    return new Promise<PluginsConfiguration>((resolve) => {
      const apiUrl = `${config.services.tangerine.baseUrl}/${orgId}/integrations`;
      fetch(apiUrl)
        .then((r) => r.json())
        .then((plugins) => resolve(plugins))
        .catch(() => {
          logger.info("Applying default plugins configuration");
          if (config.services.salesforceOrgs.includes(orgId)) {
            resolve({ orange_salesforce: {} });
          }
          resolve({});
        });
    });
  }
}
